import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitFork, Pencil, Trash2, Check, X, AlertTriangle, ShieldAlert } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Modal from "@/components/Modal";
import { useAuth } from "@/context/AuthContext";

function ForkDeleteDialog({ project, fork, onClose, onDeleted }) {
  const [check, setCheck] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/projects/${project.id}/dwellings/${fork.id}/delete-check`).then((r) => setCheck(r.data)).catch((e) => toast.error(apiError(e)));
  }, [project.id, fork.id]);

  const del = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/projects/${project.id}/dwellings/${fork.id}`, { params: { confirm_name: typed } });
      const fd = r.data.fork_deletion || {};
      toast.success(`Empty fork "${fork.name}" removed`);
      onDeleted(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const canDelete = check?.eligible && typed.trim() === (fork.name || "").trim() && !busy;
  return (
    <Modal title={`Delete Fork — ${fork.name}`} onClose={onClose}>
      <div data-testid="fork-delete-dialog">
        {!check && <div className="py-6 text-center text-sm text-gray-400">Checking dependencies…</div>}
        {check && !check.eligible && (
          <div className="rounded-md border border-red-300 bg-red-50 px-4 py-3" data-testid="fork-delete-blocked">
            <div className="flex items-center gap-2 text-sm font-bold text-red-800"><ShieldAlert size={16} /> Deletion blocked</div>
            <p className="mt-1 text-xs text-red-700">Only an empty additional fork can be deleted. This fork has associated data (or is an original stream) and must be kept — there is no override. Nothing has been changed.</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-red-800">
              {check.blockers.map((b, i) => <li key={i} data-testid="fork-delete-blocker">{b}</li>)}
            </ul>
          </div>
        )}
        {check && check.eligible && (
          <>
            <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3" data-testid="fork-delete-warning">
              <div className="flex items-center gap-2 text-sm font-bold text-amber-900"><AlertTriangle size={16} /> Remove this empty additional fork</div>
              <p className="mt-1 text-xs leading-relaxed text-amber-800">
                <strong>{fork.name}</strong> was verified server-side as an additional fork with no saved CES, submission, retailer, connection or Solar data, no documents, no VQR Costs and no variations or claims. Only its blank fork record is removed — nothing else is deleted or changed. Meters Connected and project status are recalculated.
              </p>
            </div>
            <div className="mt-4">
              <div className="bdx-label mb-1.5">Type the fork name exactly to confirm: <span className="font-mono-data normal-case text-gray-900">{fork.name}</span></div>
              <input data-testid="fork-delete-confirm-input" className="bdx-input font-mono-data" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={fork.name} autoFocus />
            </div>
          </>
        )}
        <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button data-testid="fork-delete-cancel" className="bdx-btn-secondary" onClick={onClose}>Cancel</button>
          {check?.eligible && (
            <button data-testid="fork-delete-confirm-btn" className="bdx-btn border-red-600 bg-red-600 text-white hover:bg-red-700 disabled:opacity-40" disabled={!canDelete} onClick={del}>
              {busy ? "Deleting…" : "Delete Empty Fork"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ForkRow({ project, fork, refresh }) {
  const { readOnly } = useAuth();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(fork.name);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const rename = async () => {
    if (!name.trim() || name.trim() === fork.name) { setEditing(false); setName(fork.name); return; }
    setBusy(true);
    try {
      await api.patch(`/projects/${project.id}/dwellings/${fork.id}/rename`, { name: name.trim() });
      toast.success(`Fork renamed to "${name.trim()}"`);
      setEditing(false);
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const state = fork.connection?.completed ? "Connected" : fork.future_by_others ? "Future Connection By Others" : fork.existing_connection ? "Existing Connection Complete" : "In Progress";
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`fork-row-${fork.id}`}>
      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <input data-testid={`fork-rename-input-${fork.id}`} className="bdx-input h-8 text-sm" value={name} autoFocus maxLength={60}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") rename(); if (e.key === "Escape") { setEditing(false); setName(fork.name); } }} />
            <button data-testid={`fork-rename-save-${fork.id}`} className="bdx-icon-btn h-8 w-8" title="Save name" disabled={busy || !name.trim()} onClick={rename}><Check size={13} /></button>
            <button className="bdx-icon-btn h-8 w-8" title="Cancel" onClick={() => { setEditing(false); setName(fork.name); }}><X size={13} /></button>
          </div>
        ) : (
          <>
            <div className="truncate text-sm font-semibold text-gray-950" data-testid={`fork-name-${fork.id}`}>{fork.name}</div>
            <div className="text-[10px] text-gray-500">{state}{fork.custom ? " · added fork" : " · original stream"}</div>
          </>
        )}
      </div>
      {!readOnly && !editing && (
        <div className="flex shrink-0 items-center gap-0.5">
          <button data-testid={`fork-rename-${fork.id}`} className="bdx-icon-btn h-7 w-7" title="Rename fork" onClick={() => setEditing(true)}><Pencil size={13} strokeWidth={1.6} /></button>
          {fork.custom === true && (
            <button data-testid={`fork-delete-${fork.id}`} className="bdx-icon-btn-danger h-7 w-7" title="Delete empty additional fork" onClick={() => setDeleting(true)}><Trash2 size={13} strokeWidth={1.6} /></button>
          )}
        </div>
      )}
      {deleting && <ForkDeleteDialog project={project} fork={fork} onClose={() => setDeleting(false)} onDeleted={() => { setDeleting(false); refresh(); }} />}
    </li>
  );
}

export default function ForkManagement({ project, refresh }) {
  const { readOnly } = useAuth();
  const [forkName, setForkName] = useState("");
  const [busy, setBusy] = useState(false);
  const forks = project.dwellings || [];
  const locked = project.archived || project.status === "Completed";

  const addFork = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/dwellings`, { name: forkName.trim() });
      toast.success(`Fork "${forkName.trim()}" added`);
      setForkName("");
      refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-5 border-t-2 border-[#d9cde5] pt-4" data-testid="fork-management-section">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase text-[#604184]" style={{ letterSpacing: "0.12em" }}><GitFork size={13} strokeWidth={2} /> Fork Management</div>
      <p className="mb-3 mt-1 text-[11px] leading-relaxed text-gray-500">
        Dwelling / connection streams of this {project.project_type} project. Each fork follows the full lifecycle (CES → Distributor Submission → Retailer → Connection) and counts towards Meters Connected and completion. Only an added fork with no data can be deleted; original streams and forks with data are kept.
        {readOnly && " Fork management is restricted to BDXGroup Admin."}
      </p>
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 bg-[#faf9f8]" data-testid="fork-list">
        {forks.map((d) => <ForkRow key={d.id} project={project} fork={d} refresh={refresh} />)}
      </ul>
      {!readOnly && (
        locked ? (
          <p className="mt-3 text-[11px] text-amber-700" data-testid="fork-management-locked">This project is {project.archived ? "Archived" : "Completed"} — forks cannot be added or deleted without first reopening it through the authorised workflow.</p>
        ) : (
          <div className="mt-3 flex gap-2">
            <input data-testid="gmp-fork-name" className="bdx-input" placeholder={project.project_type === "SSH" ? "e.g. Unit 1 / Public Meter" : "e.g. Unit 5 / Common Area Meter"} value={forkName} maxLength={60}
              onChange={(e) => setForkName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && forkName.trim() && !busy) addFork(); }} />
            <button type="button" data-testid="gmp-add-fork-btn" className="bdx-btn-secondary shrink-0" disabled={busy || !forkName.trim()} onClick={addFork}>{busy ? "Adding…" : "Add Fork"}</button>
          </div>
        )
      )}
    </div>
  );
}
